// 点赞/关注/收藏 乐观更新
import { useCallback, useEffect, useRef, useState } from 'react'

export interface IStatusCountUpdaterParams {
  status?: boolean
  count?: number
}

export function useStatusCountUpdater({
  status = false,
  count = 0,
}: IStatusCountUpdaterParams) {
  const [curStatus, setCurStatus] = useState(status)

  useEffect(() => {
    setCurStatus(status)
  }, [status])

  const [curCount, setCurCount] = useState(count)

  useEffect(() => {
    setCurCount(count)
  }, [count])

  return {
    begin: useCallback<
      () => {
        prev: [boolean, number]
        next: [boolean, number]
      }
    >(() => {
      setCurStatus(!curStatus)
      const count = Math.max(0, curStatus ? curCount - 1 : curCount + 1)
      setCurCount(count)
      return {
        prev: [curStatus, curCount],
        next: [!curStatus, count],
      }
    }, [curStatus, curCount]),
    rollback: useCallback(([curStatus, curCount]) => {
      setCurStatus(curStatus)
      setCurCount(curCount)
    }, []),
    curStatus,
    curCount,
  }
}

function useOptimisticStatus<T>({
  begin,
  execute,
  rollback,
}: {
  begin(): {
    prev: T
    next: T
  }
  execute(status: T): Promise<boolean> | void // 请求方法，返回boolean表示执行成功还是失败
  rollback(status: T): void
}) {
  const pendingRef = useRef(false)

  return useCallback(async () => {
    const { prev, next } = begin()
    if (execute) {
      if (pendingRef.current) {
        return
      }
      pendingRef.current = true
      try {
        const status = await execute(next)
        pendingRef.current = false
        if (status) {
          begin()
        } else {
          rollback(prev)
        }
      } catch (e) {
        pendingRef.current = false
        rollback(prev)
      }
    }
  }, [execute, begin, rollback])
}

export default useOptimisticStatus
